/**
 * The user-settings catalog (register §41, D-US1): what Cleo may list, read,
 * and change, DERIVED from the typed settings contracts at read time — never
 * a hand-kept list. Every client and server field must own exactly one
 * descriptor here (the `satisfies Record<keyof …>` guard refuses a missing or
 * extra key at compile time; `assertCatalogMatchesContracts` refuses it at
 * load; US-ANTI-02 refuses it in the loop). Enum values come from the
 * contract's own `Literals`, ranges from the exported MIN/MAX constants,
 * defaults from the decoded default documents. World-lane and profile
 * entries are declared here because they live on the World authority, not
 * in a local file; their doors are named so a caller never guesses.
 */
import * as Duration from "effect/Duration";
import * as Exit from "effect/Exit";
import * as Schema from "effect/Schema";

import { ThreadEnvMode } from "./environment.ts";
import { MusicProviderId } from "./musicProtocol.ts";
import { RuntimeMode } from "./orchestration.ts";
import {
  BackgroundActivityProfile,
  ClientSettingsSchema,
  DEFAULT_CLIENT_SETTINGS,
  DEFAULT_SERVER_SETTINGS,
  EnvironmentIdentificationMode,
  MAX_CODE_FONT_SIZE,
  MAX_GLASS_OPACITY,
  MAX_INTERFACE_FONT_SIZE,
  MAX_PROMPT_FONT_SIZE,
  MAX_SIDEBAR_AUTO_SETTLE_AFTER_DAYS,
  MAX_TERMINAL_FONT_SIZE,
  MIN_CODE_FONT_SIZE,
  MIN_GLASS_OPACITY,
  MIN_INTERFACE_FONT_SIZE,
  MIN_PROMPT_FONT_SIZE,
  MIN_SIDEBAR_AUTO_SETTLE_AFTER_DAYS,
  MIN_TERMINAL_FONT_SIZE,
  ResponseStreamingMode,
  ServerSettings,
  SessionNotificationMode,
  SessionNotificationSound,
  ShellGlass,
  SidebarProjectGroupingMode,
  SidebarProjectSortOrder,
  SidebarThreadSortOrder,
  ThemeContrast,
  ThemeDensity,
  TimestampFormat,
  WorkingIndicatorKind,
  WorkingIndicatorLoader,
  WorkingIndicatorOrbLoop,
  type ClientSettings,
  type ServerSettings as ServerSettingsType,
} from "./settings.ts";

export type SettingsLane = "you" | "world" | "mac";
export type SettingsStore = "client" | "server" | "world";

export interface SettingsCustody {
  /** Who may change the value. */
  readonly owner: "you" | "world" | "mac";
  /** Who can see it. */
  readonly visibility: "only-you" | "members" | "leaves-mac";
  /** Where it persists when that is not where a person would assume. */
  readonly storedOn?: "mac";
}

export type SettingsValueType =
  | { readonly kind: "boolean" }
  | { readonly kind: "enum"; readonly values: ReadonlyArray<string>; readonly nullable?: boolean }
  | {
      readonly kind: "integer";
      readonly min: number;
      readonly max: number;
      readonly nullable?: boolean;
    }
  | {
      readonly kind: "number";
      readonly min: number;
      readonly max: number;
      readonly nullable?: boolean;
    }
  | { readonly kind: "duration-ms" }
  | {
      readonly kind: "string";
      readonly maxLength?: number;
      readonly nullable?: boolean;
      readonly format?: string;
    }
  | { readonly kind: "text"; readonly maxChars: number }
  | { readonly kind: "string-list" }
  | { readonly kind: "record"; readonly note: string }
  | { readonly kind: "object"; readonly note: string }
  | { readonly kind: "model-selection"; readonly nullable?: boolean }
  /** AU13: names and presence only. Never a value. */
  | { readonly kind: "custody"; readonly custodyClass: string; readonly note: string };

export interface SettingsCatalogDefinition {
  readonly key: string;
  readonly lane: SettingsLane;
  readonly store: SettingsStore;
  readonly type: SettingsValueType;
  readonly default: unknown;
  readonly custody: SettingsCustody;
  /** The door a change rides (World lane) — a named tool or route, never a guess. */
  readonly door: string | null;
  readonly description: string | null;
  /**
   * May a cloud conversation (Pocket, through the Desk tool bridge) read this value? Default false.
   * Only plain personal appearance and behaviour scalars: a switch, a choice from a fixed list, or a
   * number. Never a path, URL, binding, override, account or provider reference, an identifier of
   * another World or room, free text, or any object-valued setting. Guarded at load by
   * `cloudReadableViolations`.
   */
  readonly cloudReadable?: true;
}

interface Descriptor {
  readonly lane: SettingsLane;
  readonly type: SettingsValueType;
  readonly cloudReadable?: true;
  readonly custody?: SettingsCustody;
  readonly description?: string;
}

const YOURS_ON_MAC: SettingsCustody = { owner: "you", visibility: "only-you", storedOn: "mac" };
const ONLY_THIS_MAC: SettingsCustody = { owner: "mac", visibility: "only-you" };
const LEAVES_MAC: SettingsCustody = { owner: "mac", visibility: "leaves-mac" };

const bool: SettingsValueType = { kind: "boolean" };
const enumOf = (
  schema: { readonly literals: ReadonlyArray<string> },
  nullable?: boolean,
): SettingsValueType =>
  nullable
    ? { kind: "enum", values: schema.literals, nullable: true }
    : { kind: "enum", values: schema.literals };
const int = (min: number, max: number): SettingsValueType => ({ kind: "integer", min, max });
const fontFamily: SettingsValueType = { kind: "string", maxLength: 200 };

const CLIENT_DESCRIPTORS = {
  lightStudy: {
    lane: "you",
    cloudReadable: true,
    type: {
      kind: "enum",
      values: ["none", "downlight", "pool", "rim", "wash", "ambient", "combined"],
    },
  },
  lightIntensity: {
    lane: "you",
    type: { kind: "enum", values: ["whisper", "quiet", "present"] },
    cloudReadable: true,
  },
  lightBreathe: { lane: "you", type: bool, cloudReadable: true },
  hoverLight: { lane: "you", type: bool, cloudReadable: true },
  opaqueDoors: { lane: "you", type: bool, cloudReadable: true },
  doorFade: { lane: "you", type: bool, cloudReadable: true },
  cardGlow: { lane: "you", type: int(0, 100), cloudReadable: true },
  ambientMotion: {
    lane: "you",
    type: bool,
    cloudReadable: true,
    description: "Ambient shell motion (reduced-motion always wins).",
  },
  workingIndicator: {
    lane: "you",
    type: enumOf(WorkingIndicatorKind),
    description:
      "What shows while Cleo works: the thinking orb, a loader, or the quiet dots (ST10).",
  },
  workingIndicatorOrbLoop: {
    lane: "you",
    type: enumOf(WorkingIndicatorOrbLoop),
    description: "Which of the 38 orb loops the thinking orb plays (ST10).",
  },
  workingIndicatorLoader: {
    lane: "you",
    type: enumOf(WorkingIndicatorLoader),
    description: "Which of the 37 loaders shows when the loader is chosen (ST10).",
  },
  cleoPanelWide: {
    lane: "mac",
    type: bool,
    description:
      "Widen Cleo: her panel asks for twice its usual width (960px), folding the context rail and then the dock as the window needs; off puts every panel back. The panel's own button and View > Widen/Restore Cleo change the same switch.",
  },
  confirmThreadArchive: { lane: "mac", type: bool },
  confirmThreadDelete: { lane: "mac", type: bool },
  diffIgnoreWhitespace: { lane: "mac", type: bool },
  density: { lane: "you", type: enumOf(ThemeDensity), cloudReadable: true },
  // HIG3 (GOALS/GOALS-DESIGN.html#hig3): the person's contrast choice.
  contrast: { lane: "you", type: enumOf(ThemeContrast), cloudReadable: true },
  environmentIdentificationMode: { lane: "you", type: enumOf(EnvironmentIdentificationMode) },
  glassOpacity: {
    lane: "you",
    type: int(MIN_GLASS_OPACITY, MAX_GLASS_OPACITY),
    cloudReadable: true,
  },
  fontSizeInterface: {
    lane: "you",
    type: int(MIN_INTERFACE_FONT_SIZE, MAX_INTERFACE_FONT_SIZE),
    cloudReadable: true,
    description: "Interface font size in CSS px.",
  },
  fontSizePrompt: {
    lane: "you",
    type: int(MIN_PROMPT_FONT_SIZE, MAX_PROMPT_FONT_SIZE),
    cloudReadable: true,
  },
  fontSizeCode: {
    lane: "you",
    type: int(MIN_CODE_FONT_SIZE, MAX_CODE_FONT_SIZE),
    cloudReadable: true,
  },
  fontSizeTerminal: {
    lane: "you",
    type: int(MIN_TERMINAL_FONT_SIZE, MAX_TERMINAL_FONT_SIZE),
    cloudReadable: true,
  },
  fontFamilyCode: { lane: "you", type: fontFamily },
  fontFamilyComposer: { lane: "you", type: fontFamily },
  fontFamilySans: {
    lane: "you",
    type: fontFamily,
    description: "Interface font family; empty string = the theme default.",
  },
  fontFamilyTerminal: { lane: "you", type: fontFamily },
  fontSmoothing: { lane: "you", type: bool, cloudReadable: true },
  favorites: {
    lane: "you",
    type: { kind: "record", note: "[{provider: ProviderInstanceId, model}]" },
  },
  providerModelPreferences: {
    lane: "you",
    type: { kind: "record", note: "ProviderInstanceId -> {hiddenModels[], modelOrder[]}" },
  },
  planModeEnabled: { lane: "mac", type: bool },
  queueMessagesWhileWorking: {
    lane: "mac",
    type: bool,
    description:
      "Queue messages while Cleo works. On (default): a message sent while she is working waits as Unread and she reads it when she's done. Off: it goes to her now, without stopping her where the engine can take it, by stopping her first where it cannot.",
  },
  sidebarAutoSettleAfterDays: {
    lane: "mac",
    type: {
      kind: "number",
      min: MIN_SIDEBAR_AUTO_SETTLE_AFTER_DAYS,
      max: MAX_SIDEBAR_AUTO_SETTLE_AFTER_DAYS,
      nullable: true,
    },
  },
  sidebarProjectGroupingMode: { lane: "mac", type: enumOf(SidebarProjectGroupingMode) },
  sidebarProjectGroupingOverrides: {
    lane: "mac",
    type: { kind: "record", note: "projectKey -> grouping mode" },
  },
  sidebarProjectSortOrder: { lane: "mac", type: enumOf(SidebarProjectSortOrder) },
  sidebarThreadSortOrder: { lane: "mac", type: enumOf(SidebarThreadSortOrder) },
  // UP6: every session notification channel is opt-in on this Mac.
  sessionNotifications: {
    lane: "mac",
    type: enumOf(SessionNotificationMode),
    description:
      "How a background session's completion, failure, input or approval request reaches you: off (default), a system notification, the chosen sound, or both. Never replays history; the active session never notifies.",
  },
  sessionNotificationsInApp: {
    lane: "mac",
    type: bool,
    description:
      "Show an in-app notice with Open session while the Desk is focused and another session completes, fails or needs an answer (off by default).",
  },
  sessionNotificationBadge: {
    lane: "mac",
    type: bool,
    description:
      "Count unseen background-session notifications on the app badge or favicon; clears when the Desk is focused (off by default).",
  },
  sessionNotificationSound: {
    lane: "mac",
    type: enumOf(SessionNotificationSound),
    description: "Which bundled CC0 sound plays when sound is on.",
  },
  shellGlass: { lane: "you", type: enumOf(ShellGlass), cloudReadable: true },
  timestampFormat: { lane: "you", type: enumOf(TimestampFormat), cloudReadable: true },
  toneDownSections: { lane: "you", type: bool, cloudReadable: true },
  wordWrap: { lane: "you", type: bool, cloudReadable: true },
} satisfies Record<keyof ClientSettings, Descriptor>;

const SERVER_DESCRIPTORS = {
  musicProvider: { lane: "mac", type: enumOf(MusicProviderId, true) },
  // MUS6: a public application identifier, never a secret; empty = Neucleos's own.
  spotifyClientId: {
    lane: "mac",
    type: { kind: "string", maxLength: 64 },
    description: "The Spotify application this Mac signs in under; empty means Neucleos's own.",
  },
  enableProviderUpdateChecks: { lane: "mac", type: bool },
  telemetryEnabled: {
    lane: "mac",
    type: bool,
    custody: LEAVES_MAC,
    description:
      "Anonymous analytics and content-filtered Sentry diagnostics may leave this Mac; CLEO_TELEMETRY_ENABLED=false is a hard floor.",
  },
  backgroundActivity: {
    lane: "mac",
    type: { kind: "object", note: "{profile, overrides: {intervals, pauseWhen*}}" },
  },
  automaticGitFetchInterval: { lane: "mac", type: { kind: "duration-ms" } },
  providerHealthRefreshInterval: { lane: "mac", type: { kind: "duration-ms" } },
  backgroundActivityProfile: { lane: "mac", type: enumOf(BackgroundActivityProfile) },
  defaultSessionModel: {
    lane: "mac",
    type: {
      kind: "object",
      note: "{instanceId, model, options?} or null — Neucleos Fast when null",
    },
  },
  textGenerationModelSelection: {
    lane: "mac",
    type: {
      kind: "object",
      note: "{instanceId, model, options?} or null — automatic names use message text without inference; other background generation defaults to Neucleos Fast",
    },
  },
  defaultSessionTools: {
    lane: "mac",
    type: {
      kind: "object",
      note: "legacy device preference; ignored at birth. General session defaults are World records (platform, World, person) read through neucleos_read_general_session_tools and set in Settings → Tools",
    },
  },
  defaultThreadEnvMode: { lane: "mac", type: enumOf(ThreadEnvMode) },
  // UP7: the new-session permission mode; scopable World → room → session.
  defaultRuntimeMode: {
    lane: "mac",
    type: enumOf(RuntimeMode),
    description:
      "Permission mode a new session starts in: approval-required (Supervised), auto-accept-edits, auto, or full-access. A room or World override wins; the session's own choice wins over both. Never the tool boundary.",
  },
  // UP5: how assistant text reaches the projection while a turn runs.
  responseStreamingMode: {
    lane: "mac",
    type: enumOf(ResponseStreamingMode),
    cloudReadable: true,
    description:
      "How a reply is painted while it streams: turn waits for the whole message, paragraph paints each finished paragraph or closed code block (default), token forwards every delta.",
  },
  scopedSettingOverrides: {
    lane: "mac",
    type: {
      kind: "record",
      note: "[{scope: {kind: world, worldKey} | {kind: room, worldKey, roomId}, key: defaultSessionModel | defaultRuntimeMode | defaultThreadEnvMode | responseStreamingMode, value}] — set upserts by (scope, key); scopedSettingOverrideResets removes rows so the layer above returns",
    },
    description:
      "World and room overrides of the scopable keys (UP7). Resolution is session → room → World → this Mac's plain setting; reset restores inheritance.",
  },
  newWorktreesStartFromOrigin: { lane: "mac", type: bool },
  addProjectBaseDirectory: { lane: "mac", type: { kind: "string" } },
  sourceControlWritingStyle: {
    lane: "mac",
    type: { kind: "object", note: "{mode, customInstructions, followChangeRequestTemplates}" },
  },
  providers: {
    lane: "mac",
    type: {
      kind: "custody",
      custodyClass: "provider-driver-config",
      note: "Per-driver config; any key or password inside is names-and-presence only (AU13).",
    },
  },
  providerInstances: {
    lane: "mac",
    type: {
      kind: "custody",
      custodyClass: "provider-instance-secrets",
      note: "Instance envelopes; sensitive environment values live in the secret store and are never returned (AU13).",
    },
  },
  observability: {
    lane: "mac",
    type: { kind: "object", note: "{otlpTracesUrl, otlpMetricsUrl}" },
    custody: LEAVES_MAC,
  },
  worldWorkspaceBindings: {
    lane: "mac",
    type: {
      kind: "record",
      note: "worldKey -> {sourceFolders{codebase[],files[],details{<path>:{name,description}}}, folders[], filesRoot}",
    },
  },
} satisfies Record<keyof ServerSettingsType, Descriptor>;

export const CLIENT_SETTING_KEYS: ReadonlyArray<string> = Object.keys(CLIENT_DESCRIPTORS);
export const SERVER_SETTING_KEYS: ReadonlyArray<string> = Object.keys(SERVER_DESCRIPTORS);

function laneCustody(lane: SettingsLane): SettingsCustody {
  return lane === "you" ? YOURS_ON_MAC : ONLY_THIS_MAC;
}

/** Durations and other non-JSON values become plain JSON for the door. */
export function serializeSettingValue(value: unknown): unknown {
  if (Duration.isDuration(value)) return Duration.toMillis(value);
  return value;
}

/** Load-time guard: the descriptor keys ARE the contract keys, both ways, and the two stores never share a key. */
export function assertCatalogMatchesContracts(): void {
  const drift: Array<string> = [];
  const contractClient = Object.keys(ClientSettingsSchema.fields);
  const contractServer = Object.keys(ServerSettings.fields);
  for (const key of contractClient)
    if (!(key in CLIENT_DESCRIPTORS))
      drift.push(`client contract field without catalog entry: ${key}`);
  for (const key of CLIENT_SETTING_KEYS)
    if (!contractClient.includes(key))
      drift.push(`catalog client entry without contract field: ${key}`);
  for (const key of contractServer)
    if (!(key in SERVER_DESCRIPTORS))
      drift.push(`server contract field without catalog entry: ${key}`);
  for (const key of SERVER_SETTING_KEYS)
    if (!contractServer.includes(key))
      drift.push(`catalog server entry without contract field: ${key}`);
  for (const key of CLIENT_SETTING_KEYS)
    if (contractServer.includes(key)) drift.push(`key present in both stores: ${key}`);
  if (drift.length > 0) {
    throw new Error(`settings catalog drift (US-ANTI-02): ${drift.join("; ")}`);
  }
}
assertCatalogMatchesContracts();

/**
 * Keys marked `cloudReadable` that are not a plain personal scalar: not a switch, a fixed-list choice
 * or a number; nullable; free text; kept anywhere but the two local settings files. A cloud read
 * must never carry a path, URL, binding, override, account or provider reference, another World's or
 * room's identifier, or an object.
 */
export function cloudReadableViolations(
  definitions: ReadonlyArray<SettingsCatalogDefinition>,
): ReadonlyArray<string> {
  const plain = new Set(["boolean", "enum", "integer", "number"]);
  return definitions
    .filter((definition) => definition.cloudReadable === true)
    .filter(
      (definition) =>
        !plain.has(definition.type.kind) ||
        ("nullable" in definition.type && definition.type.nullable === true) ||
        definition.store === "world" ||
        definition.custody.visibility === "leaves-mac",
    )
    .map((definition) => definition.key);
}

const WORLD_DEFINITIONS: ReadonlyArray<SettingsCatalogDefinition> = [
  {
    key: "world.presence.visibility",
    lane: "world",
    store: "world",
    type: { kind: "enum", values: ["visible", "hidden"] },
    default: "visible",
    custody: { owner: "you", visibility: "members" },
    door: "neucleos_set_presence_visibility",
    description:
      "Whether co-members of the entered World see you online (CM30). Read through neucleos_presence_heartbeat.",
  },
  {
    key: "world.receipts.default",
    lane: "world",
    store: "world",
    type: { kind: "enum", values: ["shared", "private"] },
    default: "shared",
    custody: { owner: "you", visibility: "members" },
    door: "neucleos_set_receipts_default",
    description:
      "World-wide read-receipts default; a conversation's explicit flip overrides it (CM33).",
  },
  {
    key: "world.theme.accent",
    lane: "world",
    store: "world",
    type: { kind: "object", note: "{accentHue, accentChroma} clamped to the theme manifest" },
    default: null,
    custody: { owner: "world", visibility: "members" },
    door: "POST /api/neucleos/world/theme/save (requires world.manage)",
    description: "The World's accent (TH8); an absent row is the computed default, never seeded.",
  },
  {
    key: "profile.displayName",
    lane: "you",
    store: "world",
    type: { kind: "string", maxLength: 80 },
    default: null,
    custody: { owner: "you", visibility: "members" },
    door: "neucleos_update_profile",
    description: "Your display name as members see it (actors.displayName).",
  },
  {
    key: "profile.firstName",
    lane: "you",
    store: "world",
    type: { kind: "string", nullable: true, maxLength: 80 },
    default: null,
    custody: { owner: "you", visibility: "only-you" },
    door: "neucleos_update_profile",
    description: "Your given name on the one durable Actor profile.",
  },
  {
    key: "profile.lastName",
    lane: "you",
    store: "world",
    type: { kind: "string", nullable: true, maxLength: 80 },
    default: null,
    custody: { owner: "you", visibility: "only-you" },
    door: "neucleos_update_profile",
    description: "Your family name on the one durable Actor profile.",
  },
  {
    key: "profile.phoneE164",
    lane: "you",
    store: "world",
    type: { kind: "string", nullable: true, format: "E.164" },
    default: null,
    custody: { owner: "you", visibility: "only-you" },
    door: "neucleos_update_profile",
    description: "Your cell phone in international E.164 form.",
  },
  {
    key: "profile.contactEmail",
    lane: "you",
    store: "world",
    type: { kind: "string", nullable: true, format: "email", maxLength: 254 },
    default: null,
    custody: { owner: "you", visibility: "only-you" },
    door: "neucleos_update_profile",
    description:
      "Your editable contact email; distinct from provider sign-in metadata and the system-assigned Neucleos address.",
  },
  {
    key: "profile.avatar",
    lane: "you",
    store: "world",
    type: { kind: "string", nullable: true, format: "r2-object-reference" },
    default: null,
    custody: { owner: "you", visibility: "members" },
    door: "neucleos_update_profile",
    description: "Avatar image reference on the World's object store.",
  },
  {
    key: "profile.timeZone",
    lane: "you",
    store: "world",
    type: { kind: "string", format: "IANA time zone" },
    default: null,
    custody: { owner: "you", visibility: "members" },
    door: "neucleos_update_profile",
    description: null,
  },
  {
    key: "profile.locale",
    lane: "you",
    store: "world",
    type: { kind: "string", format: "BCP 47 language tag" },
    default: null,
    custody: { owner: "you", visibility: "members" },
    door: "neucleos_update_profile",
    description: null,
  },
];

function fromDescriptors(
  store: "client" | "server",
  descriptors: Record<string, Descriptor>,
  defaults: Record<string, unknown>,
): ReadonlyArray<SettingsCatalogDefinition> {
  return Object.entries(descriptors).map(([key, descriptor]) => ({
    key,
    lane: descriptor.lane,
    store,
    type: descriptor.type,
    default: descriptor.type.kind === "custody" ? null : serializeSettingValue(defaults[key]),
    custody: descriptor.custody ?? laneCustody(descriptor.lane),
    door: null,
    description: descriptor.description ?? null,
    ...(descriptor.cloudReadable === true ? { cloudReadable: true as const } : {}),
  }));
}

/** The whole catalog, in lane order you → world → mac inside each store. */
export const SETTINGS_CATALOG: ReadonlyArray<SettingsCatalogDefinition> = [
  ...fromDescriptors(
    "client",
    CLIENT_DESCRIPTORS,
    DEFAULT_CLIENT_SETTINGS as unknown as Record<string, unknown>,
  ),
  ...fromDescriptors(
    "server",
    SERVER_DESCRIPTORS,
    DEFAULT_SERVER_SETTINGS as unknown as Record<string, unknown>,
  ),
  ...WORLD_DEFINITIONS,
];

const cloudViolations = cloudReadableViolations(SETTINGS_CATALOG);
if (cloudViolations.length > 0)
  throw new Error(
    `settings catalog: cloudReadable on a non-plain setting: ${cloudViolations.join(", ")}`,
  );

export function settingsCatalogDefinition(key: string): SettingsCatalogDefinition | null {
  return SETTINGS_CATALOG.find((entry) => entry.key === key) ?? null;
}

export type SettingValueValidation =
  | {
      readonly ok: true;
      readonly value: unknown;
      /** The text the caller sent when it plainly meant this value ("18" for 18). */
      readonly readFrom?: string;
    }
  | { readonly ok: false; readonly reason: string };

/**
 * W-1a0d442f (Dev app, Neucleos Fast, 30 September 2026): asked to set the
 * font size to 18, the model sent the text "18" in two of three runs, even
 * when told to send a number, and the door refused it. Text that plainly
 * spells a value of the setting's own type is read as that value: a
 * number's digits for a number or whole-number setting, "true" or "false"
 * for a switch. Nothing else is guessed ("18px", "large" and "" still
 * refuse), and the contract's own decoder still decides the range.
 */
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;
function readPlainValue(type: SettingsValueType, value: unknown): unknown {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if ((type.kind === "integer" || type.kind === "number") && PLAIN_NUMBER.test(text))
    return Number(text);
  if (type.kind === "boolean" && (text === "true" || text === "false")) return text === "true";
  return value;
}

/** What a setting takes, in plain words, for a refusal the model can act on. */
export function expectedValueInWords(type: SettingsValueType): string {
  const orNull = (nullable: boolean | undefined) => (nullable ? ", or null" : "");
  switch (type.kind) {
    case "boolean":
      return "true or false (not in quotes)";
    case "enum":
      return `one of ${type.values.map((value) => `"${value}"`).join(", ")}${orNull(type.nullable)}`;
    case "integer":
      return `a whole number from ${type.min} to ${type.max}, sent as a number (for example ${Math.round((type.min + type.max) / 2)}, not "${Math.round((type.min + type.max) / 2)}")${orNull(type.nullable)}`;
    case "number":
      return `a number from ${type.min} to ${type.max}, sent as a number${orNull(type.nullable)}`;
    case "duration-ms":
      return "a number of milliseconds";
    case "string":
      return `text${type.maxLength !== undefined ? ` of at most ${type.maxLength} characters` : ""}${type.format ? ` (${type.format})` : ""}${orNull(type.nullable)}`;
    case "text":
      return `text of at most ${type.maxChars} characters`;
    case "string-list":
      return "a list of text values";
    case "record":
    case "object":
      return `an object: ${type.note}`;
    case "model-selection":
      return `a model selection${orNull(type.nullable)}`;
    case "custody":
      return "nothing an agent may send";
  }
}

function describeSent(value: unknown): string {
  if (typeof value === "string") return `the text ${JSON.stringify(value)}`;
  if (value === null) return "null";
  if (Array.isArray(value)) return "a list";
  if (typeof value === "object") return "an object";
  return String(value);
}

/**
 * Validate a candidate value against the field's OWN contract schema — the
 * same decoder the settings files use — so a refusal names the contract's
 * words, never a catalog restatement.
 */
export function validateSettingValue(key: string, value: unknown): SettingValueValidation {
  const definition = settingsCatalogDefinition(key);
  if (definition === null)
    return { ok: false, reason: `unknown setting '${key}'; list_settings names the catalog` };
  if (definition.type.kind === "custody") {
    return {
      ok: false,
      reason: `'${key}' is value-opaque custody (${definition.type.custodyClass}); names and presence only (AU13)`,
    };
  }
  if (value === undefined)
    return { ok: false, reason: `'${key}' needs a value; undefined is not a change` };
  const fieldSchema =
    definition.store === "client"
      ? (ClientSettingsSchema.fields as Record<string, Schema.Top>)[key]
      : definition.store === "server"
        ? (ServerSettings.fields as Record<string, Schema.Top>)[key]
        : null;
  if (fieldSchema === null || fieldSchema === undefined) {
    return validateWorldValue(definition, value);
  }
  const candidate = readPlainValue(definition.type, value);
  const exit = Schema.decodeUnknownExit(fieldSchema as Schema.Codec<unknown, unknown>)(candidate);
  if (Exit.isSuccess(exit))
    return candidate === value
      ? { ok: true, value: exit.value }
      : { ok: true, value: exit.value, readFrom: value as string };
  return {
    ok: false,
    reason: `'${key}' takes ${expectedValueInWords(definition.type)}; it was sent ${describeSent(value)}. The contract says: ${String(exit.cause).split("\n")[0]}`,
  };
}

function validateWorldValue(
  definition: SettingsCatalogDefinition,
  value: unknown,
): SettingValueValidation {
  const type = definition.type;
  if (type.kind === "enum") {
    return typeof value === "string" && type.values.includes(value)
      ? { ok: true, value }
      : { ok: false, reason: `'${definition.key}' accepts ${type.values.join(" | ")}` };
  }
  if (type.kind === "string") {
    if (value === null && type.nullable) return { ok: true, value };
    if (typeof value !== "string")
      return {
        ok: false,
        reason: `'${definition.key}' accepts a string${type.format ? ` (${type.format})` : ""}`,
      };
    if (type.maxLength !== undefined && value.length > type.maxLength)
      return {
        ok: false,
        reason: `'${definition.key}' accepts at most ${type.maxLength} characters`,
      };
    return { ok: true, value };
  }
  if (type.kind === "object" && typeof value === "object" && value !== null)
    return { ok: true, value };
  return { ok: false, reason: `'${definition.key}' accepts ${describeType(type)}` };
}

export function describeType(type: SettingsValueType): string {
  switch (type.kind) {
    case "boolean":
      return "true | false";
    case "enum":
      return `${type.values.join(" | ")}${type.nullable ? " | null" : ""}`;
    case "integer":
      return `integer ${type.min}–${type.max}${type.nullable ? " | null" : ""}`;
    case "number":
      return `number ${type.min}–${type.max}${type.nullable ? " | null" : ""}`;
    case "duration-ms":
      return "duration in milliseconds";
    case "string":
      return `string${type.maxLength !== undefined ? ` ≤ ${type.maxLength}` : ""}${type.format ? ` (${type.format})` : ""}${type.nullable ? " | null" : ""}`;
    case "text":
      return `text ≤ ${type.maxChars} chars`;
    case "string-list":
      return "string[]";
    case "record":
      return `record ${type.note}`;
    case "object":
      return `object ${type.note}`;
    case "model-selection":
      return `ModelSelection${type.nullable ? " | null" : ""}`;
    case "custody":
      return `custody (${type.custodyClass})`;
  }
}
